import { useMemo } from "react";
import { TextField } from "@mui/material";

export interface AllowedOriginsEditorProps {
    value: string[];
    onChange(next: string[]): void;
    disabled?: boolean;
}

/**
 * One-origin-per-line input. Each line must be a valid http(s) origin —
 * `https://example.com`, `https://*.example.com`, or `https://**.example.com`.
 * Validation matches the backend `OriginValidatorService` rules (see SSO plan §5.4.1).
 * Invalid lines are surfaced in helperText but do NOT block submission;
 * the backend will return 400 with the offending value.
 */
export default function AllowedOriginsEditor({
    value,
    onChange,
    disabled
}: AllowedOriginsEditorProps) {
    const text = useMemo(() => value.join("\n"), [value]);

    const invalid = useMemo(() => {
        return value
            .map(origin => ({ origin, valid: isValidOrigin(origin) }))
            .filter(x => !x.valid)
            .map(x => x.origin);
    }, [value]);

    return (
        <TextField
            label="Allowed origins"
            multiline
            fullWidth
            minRows={4}
            value={text}
            disabled={disabled}
            onChange={e =>
                onChange(
                    e.target.value
                        .split("\n")
                        .map(s => s.trim())
                        .filter(s => s.length > 0)
                )
            }
            helperText={
                invalid.length > 0
                    ? `Invalid origin${invalid.length > 1 ? "s" : ""}: ${invalid.join(", ")}`
                    : "One per line. Wildcards: https://*.example.com or https://**.example.com."
            }
            error={invalid.length > 0}
            inputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }}
        />
    );
}

export function isValidOrigin(s: string): boolean {
    if (!s) return false;
    // Strip wildcard portion before URL parsing.
    const stripped = s.replace(/^https?:\/\/(\*+\.)/, match => {
        // Keep scheme, drop the wildcard prefix so URL() can parse.
        return match.replace(/(\*+\.)/, "");
    });
    try {
        const u = new URL(stripped);
        if (u.protocol !== "http:" && u.protocol !== "https:") return false;
        // No path / query / hash beyond root.
        if (u.pathname && u.pathname !== "/") return false;
        if (u.search) return false;
        if (u.hash) return false;
        return true;
    } catch {
        return false;
    }
}
